import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtINR, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ClipboardList, Truck, Receipt, ChevronRight, Paperclip, AlertTriangle, CheckCircle2 } from "lucide-react";
import RoleGuide from "@/components/RoleGuide";

const STATUS_STYLES = {
  PLACED:     { label: "PLACED",     cls: "bg-blue-50 border-blue-400 text-blue-800" },
  DELIVERED:  { label: "DELIVERED",  cls: "bg-amber-50 border-amber-400 text-amber-800" },
  INVOICED:   { label: "INVOICED",   cls: "bg-purple-50 border-purple-400 text-purple-800" },
  CLOSED:     { label: "CLOSED",     cls: "bg-emerald-50 border-emerald-400 text-emerald-800" },
};

export default function FuelPurchase() {
  return (
    <div className="space-y-4" data-testid="fp-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Fuel Purchase</h1>
        <p className="text-sm text-slate-600 mt-1">PO → Delivery → Invoice — one lifecycle per load.</p>
      </div>
      <RoleGuide guides={{
        manager: { freq: "as needed", title: "You raise the PO", text: "Place the PO with IOCL (fuel, qty, rate, expected date). Upload the PO PDF. When the tanker arrives, the filler / supervisor records what was actually delivered — variance auto-flags." },
        assistant_manager: { freq: "on delivery", title: "You confirm the delivery", text: "When the tanker arrives, open the matching PO and enter the actual litres received + truck number. Shortfalls > 20 L auto-flag the PO for the manager." },
        admin: { freq: "on billing", title: "You close the loop", text: "Upload IOCL's invoice PDF against the delivered PO. Amount mismatch > ₹100 raises a PO_INVOICE_VARIANCE flag naming the exact PO and variance ₹." },
        owner: { freq: "weekly", title: "Watch the leakage", text: "Any 'ordered vs delivered vs invoiced' variance is a red row on the PO list. Click to drill into the underlying documents." },
      }} />
      <Tabs defaultValue="pos">
        <TabsList>
          <TabsTrigger value="pos" data-testid="tab-pos">Purchase Orders</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">Daily Log</TabsTrigger>
        </TabsList>
        <TabsContent value="pos"><POsTab /></TabsContent>
        <TabsContent value="daily"><DailyTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function POsTab() {
  const [pos, setPos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ po_date: todayISO(), supplier: "IOCL", fuel: "HSD", ordered_qty: 0, unit_rate: 0, expected_delivery_date: "", notes: "", po_file_id: null, po_name: "" });
  const [openRowId, setOpenRowId] = useState(null);
  const poFileRef = useRef(null);

  const load = () => api.get("/manager/fuel_pos").then((r) => setPos(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const uploadPoFile = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("note", `Fuel PO — ${form.fuel} ${form.ordered_qty}L`);
    const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, po_file_id: r.data.file_id, po_name: r.data.original_name }));
    toast.success("PO file attached");
  };

  const createPO = async () => {
    if (!form.ordered_qty || Number(form.ordered_qty) <= 0) { toast.error("Ordered quantity must be > 0"); return; }
    setBusy(true);
    try {
      await api.post("/manager/fuel_pos", form);
      toast.success(`PO placed: ${form.fuel} ${form.ordered_qty}${form.fuel === "CNG" ? "kg" : "L"} @ ₹${form.unit_rate}`);
      setForm({ po_date: todayISO(), supplier: "IOCL", fuel: "HSD", ordered_qty: 0, unit_rate: 0, expected_delivery_date: "", notes: "", po_file_id: null, po_name: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const activeById = pos.find((p) => p.po_id === openRowId);

  return (
    <div className="mt-3 space-y-3">
      {/* Create PO card */}
      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <div className="flex items-center gap-2 mb-2">
          <ClipboardList size={16} className="text-slate-700" />
          <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider">Place a new Purchase Order</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">PO Date</label>
            <Input type="date" value={form.po_date} onChange={(e) => setForm({ ...form, po_date: e.target.value })} className="h-11" data-testid="po-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Supplier</label>
            <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="h-11" placeholder="IOCL" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Fuel</label>
            <div className="flex gap-1 mt-1">
              {["HSD", "MS", "CNG"].map((f) => (
                <button key={f} type="button" onClick={() => setForm({ ...form, fuel: f })}
                        data-testid={`po-fuel-${f}`}
                        className={"h-11 px-3 text-sm font-bold border-2 rounded-sm flex-1 " + (form.fuel === f ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300")}>{f}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Expected date</label>
            <Input type="date" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} className="h-11" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Ordered qty ({form.fuel === "CNG" ? "kg" : "L"})</label>
            <Input type="number" step="0.01" value={form.ordered_qty} onChange={(e) => setForm({ ...form, ordered_qty: Number(e.target.value) })} className="h-11 num" data-testid="po-qty" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500">Unit rate ₹</label>
            <Input type="number" step="0.01" value={form.unit_rate} onChange={(e) => setForm({ ...form, unit_rate: Number(e.target.value) })} className="h-11 num" data-testid="po-rate" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-500">Notes</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="PO#, indent reference, etc." className="h-11" />
          </div>
        </div>
        <div className="mt-3 flex gap-2 items-center flex-wrap">
          <input ref={poFileRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="po-file-input"
                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadPoFile(f); }} />
          <Button type="button" variant="outline" onClick={() => poFileRef.current?.click()} className="h-11 border-2 border-slate-300 font-bold">
            <Paperclip size={14} className="mr-1" /> {form.po_file_id ? "Replace PO file" : "Attach PO PDF"}
          </Button>
          {form.po_file_id && <span className="text-xs bg-emerald-50 border border-emerald-300 text-emerald-800 px-2 py-1 rounded-sm">✓ {form.po_name}</span>}
          {form.unit_rate > 0 && form.ordered_qty > 0 && (
            <span className="text-xs text-slate-600 ml-auto">Estimated bill: <strong>{fmtINR(form.unit_rate * form.ordered_qty)}</strong></span>
          )}
          <Button onClick={createPO} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white font-bold h-11" data-testid="po-create-btn">
            {busy ? "Placing…" : "Place PO"}
          </Button>
        </div>
      </Card>

      {/* PO list */}
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2 text-sm uppercase tracking-wider">Open & recent POs · {pos.length}</h3>
        {pos.length === 0 ? (
          <div className="text-sm text-slate-500 italic p-4 text-center border border-dashed border-slate-300 rounded-sm">No POs yet. Place one above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="dense-table w-full text-sm" data-testid="po-list">
              <thead>
                <tr>
                  <th>PO Date</th><th>Supplier</th><th>Fuel</th>
                  <th>Ordered</th><th>Delivered</th><th>Variance</th>
                  <th>Rate</th><th>Invoice ₹</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pos.map((p) => {
                  const st = STATUS_STYLES[p.status] || STATUS_STYLES.PLACED;
                  const varQty = p.variance_qty || 0;
                  const varAmt = p.variance_amount || 0;
                  return (
                    <tr key={p.po_id} className="hover:bg-slate-50" data-testid={`po-row-${p.po_id}`}>
                      <td>{p.po_date}</td>
                      <td>{p.supplier}</td>
                      <td className="font-bold">{p.fuel}</td>
                      <td className="num">{p.ordered_qty}</td>
                      <td className="num">{p.delivered_qty || "—"}</td>
                      <td className={"num font-bold " + (varQty < -20 ? "text-red-700" : varQty > 20 ? "text-amber-700" : "text-slate-700")}>
                        {p.status !== "PLACED" ? (varQty > 0 ? "+" : "") + varQty : "—"}
                      </td>
                      <td className="num">{p.unit_rate ? "₹" + p.unit_rate : "—"}</td>
                      <td className={"num " + (Math.abs(varAmt) > 100 ? "text-red-700 font-bold" : "")}>
                        {p.invoice_amount ? fmtINR(p.invoice_amount) : "—"}
                      </td>
                      <td>
                        <span className={"text-[10px] font-bold px-1.5 py-0.5 rounded-sm border " + st.cls}>{st.label}</span>
                      </td>
                      <td>
                        <Button size="sm" variant="outline" onClick={() => setOpenRowId(openRowId === p.po_id ? null : p.po_id)} className="border-slate-300 text-xs font-bold" data-testid={`po-open-${p.po_id}`}>
                          {openRowId === p.po_id ? "Close" : "Open"} <ChevronRight size={12} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Drawer for the open PO */}
      {activeById && <PODrawer po={activeById} onDone={() => { load(); setOpenRowId(null); }} />}
    </div>
  );
}

function PODrawer({ po, onDone }) {
  const [busy, setBusy] = useState(false);
  const [delivery, setDelivery] = useState({ delivered_date: todayISO(), delivered_qty: po.ordered_qty, truck_no: "", notes: "", delivery_note_file_id: null, delivery_name: "" });
  const [invoice, setInvoice] = useState({ invoice_no: "", invoice_date: todayISO(), invoice_amount: (po.delivered_qty || po.ordered_qty) * (po.unit_rate || 0), notes: "", invoice_file_id: null, invoice_name: "" });
  const dRef = useRef(null); const iRef = useRef(null);

  const upload = async (kind, file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("note", `Fuel PO ${po.po_id} — ${kind}`);
    const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
    if (kind === "delivery") setDelivery((d) => ({ ...d, delivery_note_file_id: r.data.file_id, delivery_name: r.data.original_name }));
    else setInvoice((i) => ({ ...i, invoice_file_id: r.data.file_id, invoice_name: r.data.original_name }));
    toast.success(`${kind} file attached`);
  };

  const saveDelivery = async () => {
    setBusy(true);
    try {
      await api.post(`/manager/fuel_pos/${po.po_id}/delivery`, { ...delivery, po_id: po.po_id });
      toast.success("Delivery recorded");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const saveInvoice = async () => {
    if (!invoice.invoice_no) { toast.error("Invoice number required"); return; }
    setBusy(true);
    try {
      await api.post(`/manager/fuel_pos/${po.po_id}/invoice`, { ...invoice, po_id: po.po_id });
      toast.success("Invoice attached");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const canClose = po.status === "INVOICED";
  const doClose = async () => {
    setBusy(true);
    try { await api.post(`/manager/fuel_pos/${po.po_id}/close`); toast.success("PO closed"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const canDeliver = po.status === "PLACED" || po.status === "DELIVERED";
  const canInvoice = po.status === "DELIVERED" || po.status === "INVOICED";

  return (
    <Card className="p-4 border-2 border-slate-900 rounded-sm space-y-4" data-testid="po-drawer">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">PO drawer · {po.po_id}</div>
          <h3 className="text-lg font-bold text-slate-900">{po.supplier} · {po.fuel} · {po.ordered_qty}{po.fuel === "CNG" ? "kg" : "L"} @ ₹{po.unit_rate || "—"}</h3>
        </div>
        <span className={"text-xs font-bold px-2 py-1 rounded-sm border-2 " + (STATUS_STYLES[po.status]?.cls || STATUS_STYLES.PLACED.cls)}>{po.status}</span>
      </div>

      {/* Step 2: Delivery */}
      <div className={"border-2 rounded-sm p-3 " + (po.status === "PLACED" ? "border-amber-400 bg-amber-50/40" : "border-slate-200")}>
        <div className="flex items-center gap-2 mb-2">
          <Truck size={16} className="text-slate-700" />
          <h4 className="font-bold text-slate-800 text-sm">Step 2 · Record delivery</h4>
          {po.status !== "PLACED" && <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} /> Recorded {po.delivered_date}</span>}
        </div>
        {canDeliver && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div><label className="text-[10px] uppercase font-bold text-slate-500">Delivered date</label><Input type="date" value={delivery.delivered_date} onChange={(e) => setDelivery({ ...delivery, delivered_date: e.target.value })} className="h-10" /></div>
            <div><label className="text-[10px] uppercase font-bold text-slate-500">Delivered qty</label><Input type="number" step="0.01" value={delivery.delivered_qty} onChange={(e) => setDelivery({ ...delivery, delivered_qty: Number(e.target.value) })} className="h-10 num" data-testid="delivery-qty" /></div>
            <div><label className="text-[10px] uppercase font-bold text-slate-500">Truck no.</label><Input value={delivery.truck_no} onChange={(e) => setDelivery({ ...delivery, truck_no: e.target.value.toUpperCase() })} className="h-10 uppercase" placeholder="MH-04-XX-1234" /></div>
            <div><label className="text-[10px] uppercase font-bold text-slate-500">Note</label><Input value={delivery.notes} onChange={(e) => setDelivery({ ...delivery, notes: e.target.value })} className="h-10" /></div>
            <div className="col-span-full flex flex-wrap gap-2 items-center">
              <input ref={dRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload("delivery", f); }} data-testid="delivery-file-input" />
              <Button variant="outline" onClick={() => dRef.current?.click()} className="h-10 border-2 border-slate-300 text-xs font-bold"><Paperclip size={12} className="mr-1" /> {delivery.delivery_note_file_id ? "Replace note" : "Delivery note"}</Button>
              {delivery.delivery_note_file_id && <span className="text-xs bg-emerald-50 border border-emerald-300 text-emerald-800 px-2 py-1 rounded-sm">✓ {delivery.delivery_name}</span>}
              {delivery.delivered_qty && po.ordered_qty && (
                <span className={"text-xs ml-auto " + (Math.abs(delivery.delivered_qty - po.ordered_qty) > 20 ? "text-red-700 font-bold flex items-center gap-1" : "text-slate-600")}>
                  {Math.abs(delivery.delivered_qty - po.ordered_qty) > 20 && <AlertTriangle size={12} />}
                  Variance: {(delivery.delivered_qty - po.ordered_qty).toFixed(2)}{po.fuel === "CNG" ? " kg" : " L"}
                </span>
              )}
              <Button onClick={saveDelivery} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white font-bold h-10" data-testid="po-save-delivery">Record delivery</Button>
            </div>
          </div>
        )}
      </div>

      {/* Step 3: Invoice */}
      <div className={"border-2 rounded-sm p-3 " + (po.status === "DELIVERED" ? "border-purple-400 bg-purple-50/40" : "border-slate-200")}>
        <div className="flex items-center gap-2 mb-2">
          <Receipt size={16} className="text-slate-700" />
          <h4 className="font-bold text-slate-800 text-sm">Step 3 · Attach IOCL invoice</h4>
          {po.status === "INVOICED" || po.status === "CLOSED" ? <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} /> {po.invoice_no}</span> : null}
        </div>
        {canInvoice && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div><label className="text-[10px] uppercase font-bold text-slate-500">Invoice #</label><Input value={invoice.invoice_no} onChange={(e) => setInvoice({ ...invoice, invoice_no: e.target.value })} className="h-10" data-testid="invoice-no" /></div>
            <div><label className="text-[10px] uppercase font-bold text-slate-500">Invoice date</label><Input type="date" value={invoice.invoice_date} onChange={(e) => setInvoice({ ...invoice, invoice_date: e.target.value })} className="h-10" /></div>
            <div><label className="text-[10px] uppercase font-bold text-slate-500">Invoice ₹</label><Input type="number" step="0.01" value={invoice.invoice_amount} onChange={(e) => setInvoice({ ...invoice, invoice_amount: Number(e.target.value) })} className="h-10 num" data-testid="invoice-amount" /></div>
            <div><label className="text-[10px] uppercase font-bold text-slate-500">Note</label><Input value={invoice.notes} onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })} className="h-10" /></div>
            <div className="col-span-full flex flex-wrap gap-2 items-center">
              <input ref={iRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload("invoice", f); }} data-testid="invoice-file-input" />
              <Button variant="outline" onClick={() => iRef.current?.click()} className="h-10 border-2 border-slate-300 text-xs font-bold"><Paperclip size={12} className="mr-1" /> {invoice.invoice_file_id ? "Replace invoice" : "Attach invoice PDF"}</Button>
              {invoice.invoice_file_id && <span className="text-xs bg-emerald-50 border border-emerald-300 text-emerald-800 px-2 py-1 rounded-sm">✓ {invoice.invoice_name}</span>}
              <Button onClick={saveInvoice} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white font-bold h-10" data-testid="po-save-invoice">Attach invoice</Button>
            </div>
          </div>
        )}
      </div>

      {canClose && (
        <div className="flex justify-end">
          <Button onClick={doClose} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="po-close-btn">
            <CheckCircle2 size={14} className="mr-1" /> Close PO
          </Button>
        </div>
      )}
    </Card>
  );
}

function DailyTab() {
  const [rows, setRows] = useState([]);
  const [entry, setEntry] = useState({ date: todayISO(), hsd_purchase: 0, ms_purchase: 0, cng_purchase: 0, hsd_sales: 0, ms_sales: 0, cng_sales: 0, bank_balance: 0, comment: "", book_photo_file_id: null, book_photo_name: "" });
  const [uploadingBook, setUploadingBook] = useState(false);
  const load = () => api.get("/manager/fuel_purchase").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = { ...entry };
      delete payload.book_photo_name;
      await api.post("/manager/fuel_purchase", payload);
      toast.success(`Saved log for ${entry.date} — refreshed below.`);
      // v17.11 (2026-08-19) bug 5: refresh the log table AND reset the form
      // to a fresh row so the user sees their save landed in the history.
      await load();
      setEntry({
        date: todayISO(), hsd_purchase: 0, ms_purchase: 0, cng_purchase: 0,
        hsd_sales: 0, ms_sales: 0, cng_sales: 0, bank_balance: 0, comment: "",
        book_photo_file_id: null, book_photo_name: "",
      });
    } catch (e) { toast.error(apiError(e)); }
  };
  // Phase-3 B3 — separate upload for the paper-book page.
  const uploadBookPhoto = async (file) => {
    setUploadingBook(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (entry.date) fd.append("date", entry.date);
      fd.append("note", `Load-to-load book page — ${entry.date}`);
      const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEntry((s) => ({ ...s, book_photo_file_id: r.data.file_id, book_photo_name: r.data.original_name }));
      toast.success("Handwritten-book photo attached");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploadingBook(false); }
  };
  return (
    <div className="mt-3 space-y-3">
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2 text-sm uppercase tracking-wider">Daily fuel reconciliation log</h3>
        <div className="text-[11px] text-slate-500 mb-2">
          Purchases delivered today (from IOCL tanker); typed litres/kg for
          each fuel; end-of-day bank balance. Every field is labelled — no
          bare boxes. All values append to the log below immediately on Save.
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Date</label>
            <Input type="date" value={entry.date} onChange={(e) => setEntry({ ...entry, date: e.target.value })} className="h-11 border border-slate-300" data-testid="fp-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">HSD Purchase (L)</label>
            <Input type="number" step="0.01" value={entry.hsd_purchase} onChange={(e) => setEntry({ ...entry, hsd_purchase: Number(e.target.value) })} className="h-11 num" data-testid="fp-hsd-purchase" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">MS Purchase (L)</label>
            <Input type="number" step="0.01" value={entry.ms_purchase} onChange={(e) => setEntry({ ...entry, ms_purchase: Number(e.target.value) })} className="h-11 num" data-testid="fp-ms-purchase" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">CNG Purchase (kg)</label>
            <Input type="number" step="0.01" value={entry.cng_purchase} onChange={(e) => setEntry({ ...entry, cng_purchase: Number(e.target.value) })} className="h-11 num" data-testid="fp-cng-purchase" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Bank Balance (₹)</label>
            <Input type="number" step="0.01" value={entry.bank_balance} onChange={(e) => setEntry({ ...entry, bank_balance: Number(e.target.value) })} className="h-11 num" data-testid="fp-bank-balance" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Comment</label>
            <Input placeholder="e.g. tanker no. / driver name" value={entry.comment} onChange={(e) => setEntry({ ...entry, comment: e.target.value })} className="h-11" data-testid="fp-comment" />
          </div>
        </div>

        {/* Phase-3 B3: photo of the handwritten register page (same intent as Dip-to-Dip). */}
        <div className="mt-3 bg-white p-3 border-2 border-dashed border-slate-400 rounded-sm">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Handwritten register page</div>
          <div className="text-[11px] text-slate-500 mb-2">
            Photo of the day&rsquo;s row in the paper book. Manager verifies
            typed litres/kg back against this before signing off.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1 text-xs font-bold px-3 py-2 bg-white border-2 border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50" data-testid="fp-book-label">
              <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" data-testid="fp-book-input"
                     onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadBookPhoto(f); }} />
              {uploadingBook ? "Uploading…" : (entry.book_photo_file_id ? "Replace book photo" : "Take book-page photo")}
            </label>
            {entry.book_photo_file_id && (
              <span className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm inline-flex items-center gap-1" data-testid="fp-book-ok">
                ✓ {entry.book_photo_name}
              </span>
            )}
          </div>
        </div>

        <Button className="mt-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={save} data-testid="fp-save-btn">Save daily log</Button>
      </Card>
      <Card className="p-3 border-2 border-slate-300 rounded-sm" data-testid="fp-log-history">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Log history · newest first ({rows.length} rows)</h4>
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Date</th><th>HSD (L)</th><th>MS (L)</th><th>CNG (kg)</th><th>Bank (₹)</th><th>Comment</th></tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-xs text-slate-500 italic py-2">No entries yet — save your first one above.</td></tr>
            )}
            {rows.slice(0, 30).map((r) => (
              <tr key={r.date} data-testid={`fp-log-row-${r.date}`}>
                <td className="font-bold">{r.date}</td>
                <td className="num">{r.hsd_purchase}</td>
                <td className="num">{r.ms_purchase}</td>
                <td className="num">{r.cng_purchase}</td>
                <td className="num">{fmtINR(r.bank_balance)}</td>
                <td className="text-xs text-slate-600 truncate max-w-[200px]">{r.comment || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
